// src/lib/taxonomy/naics-relationships.ts
// NAICS code relationships for capability inference
// Focus: IT services, professional services, construction (common federal categories)

import { NaicsRelationship, NaicsCategory } from './types';

export const NAICS_RELATIONSHIPS: NaicsRelationship[] = [
  // ============================================================
  // IT SERVICES (541xxx)
  // ============================================================
  {
    code: '541511',
    title: 'Custom Computer Programming Services',
    description: 'Writing, modifying, testing, and supporting software to meet the needs of a particular customer',
    category: 'it_services',
    parentCode: '5415',
    relatedCodes: ['541512', '541519', '518210'],
    proves: [
      { capability: 'custom_software_development', confidence: 0.95 },
      { capability: 'software_engineering', confidence: 0.95 },
      { capability: 'programming', confidence: 0.95 },
      { capability: 'application_development', confidence: 0.90 }
    ],
    commonKeywords: ['software development', 'programming', 'custom software', 'application development', 'coding']
  },
  {
    code: '541512',
    title: 'Computer Systems Design Services',
    description: 'Planning and designing computer systems that integrate hardware, software, and communications technologies',
    category: 'it_services',
    parentCode: '5415',
    relatedCodes: ['541511', '541513', '541519'],
    proves: [
      { capability: 'systems_integration', confidence: 0.95 },
      { capability: 'it_architecture', confidence: 0.95 },
      { capability: 'enterprise_it', confidence: 0.90 },
      { capability: 'technology_consulting', confidence: 0.85 },
      { capability: 'infrastructure_design', confidence: 0.90 }
    ],
    commonKeywords: ['systems design', 'IT consulting', 'systems integration', 'enterprise architecture', 'IT infrastructure']
  },
  {
    code: '541513',
    title: 'Computer Facilities Management Services',
    description: 'Providing on-site management and operation of clients computer systems and/or data processing facilities',
    category: 'it_services',
    parentCode: '5415',
    relatedCodes: ['541512', '518210', '541519'],
    proves: [
      { capability: 'it_operations', confidence: 0.95 },
      { capability: 'data_center_management', confidence: 0.95 },
      { capability: 'facilities_management', confidence: 0.85 },
      { capability: 'managed_services', confidence: 0.90 }
    ],
    commonKeywords: ['facilities management', 'data center', 'IT operations', 'managed services', 'infrastructure management']
  },
  {
    code: '541519',
    title: 'Other Computer Related Services',
    description: 'Computer related services not elsewhere classified including disaster recovery, software installation, computer graphics',
    category: 'it_services',
    parentCode: '5415',
    relatedCodes: ['541511', '541512', '518210'],
    proves: [
      { capability: 'it_services', confidence: 0.90 },
      { capability: 'disaster_recovery', confidence: 0.80 },
      { capability: 'technical_support', confidence: 0.85 },
      { capability: 'software_installation', confidence: 0.80 }
    ],
    commonKeywords: ['IT support', 'disaster recovery', 'technical services', 'computer services', 'software installation']
  },
  {
    code: '518210',
    title: 'Data Processing, Hosting, and Related Services',
    description: 'Providing infrastructure for hosting or data processing services',
    category: 'it_services',
    parentCode: '5182',
    relatedCodes: ['541512', '541513', '541519'],
    proves: [
      { capability: 'cloud_hosting', confidence: 0.95 },
      { capability: 'data_processing', confidence: 0.95 },
      { capability: 'managed_hosting', confidence: 0.90 },
      { capability: 'infrastructure_services', confidence: 0.90 }
    ],
    commonKeywords: ['hosting', 'data processing', 'cloud services', 'data center', 'SaaS']
  },
  {
    code: '517110',
    title: 'Wired Telecommunications Carriers',
    description: 'Operating and maintaining switching and transmission facilities to provide communications via wires',
    category: 'it_services',
    parentCode: '5171',
    relatedCodes: ['517210', '541512'],
    proves: [
      { capability: 'telecommunications', confidence: 0.95 },
      { capability: 'network_infrastructure', confidence: 0.90 },
      { capability: 'communications_services', confidence: 0.90 }
    ],
    commonKeywords: ['telecommunications', 'network', 'communications', 'wired', 'connectivity']
  },
  {
    code: '517210',
    title: 'Wireless Telecommunications Carriers',
    description: 'Operating and maintaining switching and transmission facilities for wireless communications',
    category: 'it_services',
    parentCode: '5172',
    relatedCodes: ['517110', '541512'],
    proves: [
      { capability: 'wireless_communications', confidence: 0.95 },
      { capability: 'telecommunications', confidence: 0.90 },
      { capability: 'mobile_technology', confidence: 0.85 }
    ],
    commonKeywords: ['wireless', 'mobile', 'telecommunications', 'cellular']
  },

  // ============================================================
  // PROFESSIONAL SERVICES (541xxx)
  // ============================================================
  {
    code: '541611',
    title: 'Administrative Management and General Management Consulting Services',
    description: 'Providing operating advice and assistance to businesses on administrative management issues',
    category: 'professional_services',
    parentCode: '5416',
    relatedCodes: ['541612', '541618', '541690'],
    proves: [
      { capability: 'management_consulting', confidence: 0.95 },
      { capability: 'business_process_improvement', confidence: 0.90 },
      { capability: 'organizational_development', confidence: 0.85 },
      { capability: 'strategic_planning', confidence: 0.85 }
    ],
    commonKeywords: ['management consulting', 'business consulting', 'organizational', 'strategy', 'process improvement']
  },
  {
    code: '541612',
    title: 'Human Resources Consulting Services',
    description: 'Providing advice and assistance to businesses on human resource and personnel policies',
    category: 'professional_services',
    parentCode: '5416',
    relatedCodes: ['541611', '541618', '541990'],
    proves: [
      { capability: 'hr_consulting', confidence: 0.95 },
      { capability: 'workforce_development', confidence: 0.90 },
      { capability: 'training_development', confidence: 0.85 },
      { capability: 'organizational_development', confidence: 0.80 }
    ],
    commonKeywords: ['HR', 'human resources', 'workforce', 'training', 'personnel']
  },
  {
    code: '541618',
    title: 'Other Management Consulting Services',
    description: 'Providing management consulting services not elsewhere classified',
    category: 'professional_services',
    parentCode: '5416',
    relatedCodes: ['541611', '541612', '541690'],
    proves: [
      { capability: 'management_consulting', confidence: 0.90 },
      { capability: 'specialized_consulting', confidence: 0.85 },
      { capability: 'advisory_services', confidence: 0.85 }
    ],
    commonKeywords: ['consulting', 'advisory', 'management', 'specialized']
  },
  {
    code: '541690',
    title: 'Other Scientific and Technical Consulting Services',
    description: 'Providing advice and assistance on scientific and technical issues not elsewhere classified',
    category: 'professional_services',
    parentCode: '5416',
    relatedCodes: ['541611', '541618', '541330'],
    proves: [
      { capability: 'technical_consulting', confidence: 0.95 },
      { capability: 'scientific_consulting', confidence: 0.90 },
      { capability: 'subject_matter_expertise', confidence: 0.90 },
      { capability: 'advisory_services', confidence: 0.85 }
    ],
    commonKeywords: ['technical consulting', 'scientific', 'SME', 'technical advisory', 'expertise']
  },
  {
    code: '541990',
    title: 'All Other Professional, Scientific, and Technical Services',
    description: 'Professional, scientific, and technical services not elsewhere classified',
    category: 'professional_services',
    parentCode: '5419',
    relatedCodes: ['541611', '541690', '541618'],
    proves: [
      { capability: 'professional_services', confidence: 0.85 },
      { capability: 'technical_services', confidence: 0.85 },
      { capability: 'specialized_services', confidence: 0.80 }
    ],
    commonKeywords: ['professional services', 'technical services', 'specialized']
  },

  // ============================================================
  // ENGINEERING SERVICES
  // ============================================================
  {
    code: '541330',
    title: 'Engineering Services',
    description: 'Applying physical laws and principles of engineering in the design and development of projects',
    category: 'engineering',
    parentCode: '5413',
    relatedCodes: ['541310', '541512', '541690'],
    proves: [
      { capability: 'engineering_services', confidence: 0.95 },
      { capability: 'technical_design', confidence: 0.95 },
      { capability: 'systems_engineering', confidence: 0.90 },
      { capability: 'project_engineering', confidence: 0.90 }
    ],
    commonKeywords: ['engineering', 'design', 'systems engineering', 'technical', 'development']
  },
  {
    code: '541310',
    title: 'Architectural Services',
    description: 'Planning and designing residential, institutional, commercial, and industrial buildings',
    category: 'engineering',
    parentCode: '5413',
    relatedCodes: ['541330', '541320', '236220'],
    proves: [
      { capability: 'architectural_services', confidence: 0.95 },
      { capability: 'building_design', confidence: 0.95 },
      { capability: 'space_planning', confidence: 0.85 }
    ],
    commonKeywords: ['architecture', 'building design', 'planning', 'construction design']
  },
  {
    code: '541320',
    title: 'Landscape Architectural Services',
    description: 'Planning and designing land areas for projects',
    category: 'engineering',
    parentCode: '5413',
    relatedCodes: ['541310', '541330', '561730'],
    proves: [
      { capability: 'landscape_architecture', confidence: 0.95 },
      { capability: 'site_planning', confidence: 0.90 },
      { capability: 'environmental_design', confidence: 0.80 }
    ],
    commonKeywords: ['landscape', 'site planning', 'outdoor design', 'environmental']
  },

  // ============================================================
  // RESEARCH SERVICES
  // ============================================================
  {
    code: '541714',
    title: 'Research and Development in Biotechnology',
    description: 'Conducting research and development in biotechnology',
    category: 'research',
    parentCode: '5417',
    relatedCodes: ['541715', '541720'],
    proves: [
      { capability: 'biotechnology_research', confidence: 0.95 },
      { capability: 'scientific_research', confidence: 0.95 },
      { capability: 'laboratory_services', confidence: 0.85 }
    ],
    commonKeywords: ['biotechnology', 'research', 'R&D', 'life sciences', 'laboratory']
  },
  {
    code: '541715',
    title: 'Research and Development in the Physical, Engineering, and Life Sciences',
    description: 'Conducting research and development in the physical, engineering, and life sciences',
    category: 'research',
    parentCode: '5417',
    relatedCodes: ['541714', '541720', '541330'],
    proves: [
      { capability: 'scientific_research', confidence: 0.95 },
      { capability: 'research_development', confidence: 0.95 },
      { capability: 'engineering_research', confidence: 0.90 },
      { capability: 'technical_research', confidence: 0.90 }
    ],
    commonKeywords: ['R&D', 'research', 'development', 'engineering', 'science']
  },
  {
    code: '541720',
    title: 'Research and Development in the Social Sciences and Humanities',
    description: 'Conducting research and development in the social sciences and humanities',
    category: 'research',
    parentCode: '5417',
    relatedCodes: ['541715', '541611', '541990'],
    proves: [
      { capability: 'social_science_research', confidence: 0.95 },
      { capability: 'policy_research', confidence: 0.90 },
      { capability: 'survey_research', confidence: 0.85 },
      { capability: 'program_evaluation', confidence: 0.85 }
    ],
    commonKeywords: ['social science', 'research', 'policy', 'evaluation', 'survey']
  },

  // ============================================================
  // CONSTRUCTION SERVICES
  // ============================================================
  {
    code: '236220',
    title: 'Commercial and Institutional Building Construction',
    description: 'Construction of commercial and institutional buildings',
    category: 'construction',
    parentCode: '2362',
    relatedCodes: ['236210', '237310', '238220'],
    proves: [
      { capability: 'commercial_construction', confidence: 0.95 },
      { capability: 'building_construction', confidence: 0.95 },
      { capability: 'general_contracting', confidence: 0.90 },
      { capability: 'project_management', confidence: 0.85 }
    ],
    commonKeywords: ['construction', 'building', 'commercial', 'institutional', 'general contractor']
  },
  {
    code: '236210',
    title: 'Industrial Building Construction',
    description: 'Construction of industrial buildings such as factories and warehouses',
    category: 'construction',
    parentCode: '2362',
    relatedCodes: ['236220', '237310', '238220'],
    proves: [
      { capability: 'industrial_construction', confidence: 0.95 },
      { capability: 'building_construction', confidence: 0.95 },
      { capability: 'heavy_construction', confidence: 0.85 }
    ],
    commonKeywords: ['industrial', 'factory', 'warehouse', 'construction', 'manufacturing facility']
  },
  {
    code: '237310',
    title: 'Highway, Street, and Bridge Construction',
    description: 'Construction of highways, streets, roads, and bridges',
    category: 'construction',
    parentCode: '2373',
    relatedCodes: ['237110', '237990', '236220'],
    proves: [
      { capability: 'infrastructure_construction', confidence: 0.95 },
      { capability: 'civil_construction', confidence: 0.95 },
      { capability: 'transportation_infrastructure', confidence: 0.90 },
      { capability: 'heavy_civil', confidence: 0.90 }
    ],
    commonKeywords: ['highway', 'road', 'bridge', 'infrastructure', 'civil']
  },
  {
    code: '237110',
    title: 'Water and Sewer Line and Related Structures Construction',
    description: 'Construction of water and sewer lines and related structures',
    category: 'construction',
    parentCode: '2371',
    relatedCodes: ['237310', '237990', '562910'],
    proves: [
      { capability: 'utility_construction', confidence: 0.95 },
      { capability: 'infrastructure_construction', confidence: 0.90 },
      { capability: 'water_infrastructure', confidence: 0.95 },
      { capability: 'civil_construction', confidence: 0.85 }
    ],
    commonKeywords: ['water', 'sewer', 'utility', 'pipeline', 'infrastructure']
  },

  // ============================================================
  // FACILITIES SERVICES
  // ============================================================
  {
    code: '561210',
    title: 'Facilities Support Services',
    description: 'Providing operating staff to perform support services within client facilities',
    category: 'facilities',
    parentCode: '5612',
    relatedCodes: ['561720', '561730', '561611'],
    proves: [
      { capability: 'facilities_support', confidence: 0.95 },
      { capability: 'operations_support', confidence: 0.90 },
      { capability: 'base_operations', confidence: 0.85 },
      { capability: 'facility_management', confidence: 0.90 }
    ],
    commonKeywords: ['facilities', 'support services', 'operations', 'base ops', 'facility management']
  },
  {
    code: '561720',
    title: 'Janitorial Services',
    description: 'Cleaning building interiors, windows, and the like',
    category: 'facilities',
    parentCode: '5617',
    relatedCodes: ['561210', '561730', '561790'],
    proves: [
      { capability: 'janitorial_services', confidence: 0.95 },
      { capability: 'building_maintenance', confidence: 0.85 },
      { capability: 'cleaning_services', confidence: 0.95 }
    ],
    commonKeywords: ['janitorial', 'cleaning', 'custodial', 'maintenance']
  },
  {
    code: '561730',
    title: 'Landscaping Services',
    description: 'Providing landscape care and maintenance services',
    category: 'facilities',
    parentCode: '5617',
    relatedCodes: ['561720', '541320', '561210'],
    proves: [
      { capability: 'landscaping_services', confidence: 0.95 },
      { capability: 'grounds_maintenance', confidence: 0.95 },
      { capability: 'exterior_maintenance', confidence: 0.85 }
    ],
    commonKeywords: ['landscaping', 'grounds', 'lawn care', 'exterior maintenance']
  },

  // ============================================================
  // ADMINISTRATIVE SERVICES
  // ============================================================
  {
    code: '561110',
    title: 'Office Administrative Services',
    description: 'Providing operating day-to-day administrative services',
    category: 'administrative',
    parentCode: '5611',
    relatedCodes: ['561210', '561410', '541611'],
    proves: [
      { capability: 'administrative_services', confidence: 0.95 },
      { capability: 'office_management', confidence: 0.90 },
      { capability: 'business_support', confidence: 0.85 }
    ],
    commonKeywords: ['administrative', 'office services', 'business support', 'admin']
  },
  {
    code: '561410',
    title: 'Document Preparation Services',
    description: 'Providing document preparation services including word processing, desktop publishing',
    category: 'administrative',
    parentCode: '5614',
    relatedCodes: ['561110', '541611', '518210'],
    proves: [
      { capability: 'document_services', confidence: 0.95 },
      { capability: 'word_processing', confidence: 0.90 },
      { capability: 'desktop_publishing', confidence: 0.85 }
    ],
    commonKeywords: ['document', 'word processing', 'desktop publishing', 'typing']
  },
  {
    code: '561611',
    title: 'Investigation Services',
    description: 'Providing investigation and detective services',
    category: 'administrative',
    parentCode: '5616',
    relatedCodes: ['561612', '561621', '541990'],
    proves: [
      { capability: 'investigation_services', confidence: 0.95 },
      { capability: 'background_checks', confidence: 0.90 },
      { capability: 'due_diligence', confidence: 0.85 }
    ],
    commonKeywords: ['investigation', 'detective', 'background', 'security']
  },
  {
    code: '561612',
    title: 'Security Guards and Patrol Services',
    description: 'Providing security guard and patrol services',
    category: 'administrative',
    parentCode: '5616',
    relatedCodes: ['561611', '561621', '561210'],
    proves: [
      { capability: 'security_services', confidence: 0.95 },
      { capability: 'physical_security', confidence: 0.95 },
      { capability: 'guard_services', confidence: 0.95 }
    ],
    commonKeywords: ['security', 'guards', 'patrol', 'protection']
  },
  {
    code: '561621',
    title: 'Security Systems Services (except Locksmiths)',
    description: 'Selling security systems and related equipment with installation and maintenance',
    category: 'administrative',
    parentCode: '5616',
    relatedCodes: ['561612', '541512', '517110'],
    proves: [
      { capability: 'security_systems', confidence: 0.95 },
      { capability: 'electronic_security', confidence: 0.95 },
      { capability: 'security_installation', confidence: 0.90 }
    ],
    commonKeywords: ['security systems', 'access control', 'CCTV', 'surveillance', 'alarm']
  }
];

/**
 * Find a NAICS relationship by code
 */
export function findNaics(code: string): NaicsRelationship | undefined {
  return NAICS_RELATIONSHIPS.find(naics => naics.code === code);
}

/**
 * Find NAICS by partial code match (e.g., "5415" matches all 5415xx codes)
 */
export function findNaicsByPrefix(prefix: string): NaicsRelationship[] {
  return NAICS_RELATIONSHIPS.filter(naics => naics.code.startsWith(prefix));
}

/**
 * Search NAICS by keyword
 */
export function searchNaicsByKeyword(keyword: string): NaicsRelationship[] {
  const normalizedKeyword = keyword.toLowerCase().trim();

  return NAICS_RELATIONSHIPS.filter(naics =>
    naics.title.toLowerCase().includes(normalizedKeyword) ||
    naics.description.toLowerCase().includes(normalizedKeyword) ||
    naics.commonKeywords.some(k => k.toLowerCase().includes(normalizedKeyword))
  );
}

/**
 * Get NAICS by category
 */
export function getNaicsByCategory(category: NaicsCategory): NaicsRelationship[] {
  return NAICS_RELATIONSHIPS.filter(naics => naics.category === category);
}

/**
 * Get related NAICS codes
 */
export function getRelatedNaics(code: string): NaicsRelationship[] {
  const naics = findNaics(code);
  if (!naics) return [];

  return naics.relatedCodes
    .map(relatedCode => findNaics(relatedCode))
    .filter((n): n is NaicsRelationship => n !== undefined);
}

/**
 * Calculate NAICS similarity score between two codes
 * Returns 0-1 where 1 is exact match
 */
export function calculateNaicsSimilarity(code1: string, code2: string): number {
  if (code1 === code2) return 1.0;

  // Same 5-digit prefix
  if (code1.substring(0, 5) === code2.substring(0, 5)) return 0.9;

  // Same 4-digit prefix
  if (code1.substring(0, 4) === code2.substring(0, 4)) return 0.8;

  // Same 3-digit prefix
  if (code1.substring(0, 3) === code2.substring(0, 3)) return 0.6;

  // Same 2-digit prefix (same sector)
  if (code1.substring(0, 2) === code2.substring(0, 2)) return 0.4;

  // Check if they're in related codes list
  const naics1 = findNaics(code1);
  const naics2 = findNaics(code2);

  if (naics1?.relatedCodes.includes(code2) || naics2?.relatedCodes.includes(code1)) {
    return 0.5;
  }

  // Different sectors
  return 0.1;
}

/**
 * Get all capability IDs that NAICS codes can prove
 */
export function getAllNaicsCapabilities(): string[] {
  const capabilities = new Set<string>();

  for (const naics of NAICS_RELATIONSHIPS) {
    for (const proof of naics.proves) {
      capabilities.add(proof.capability);
    }
  }

  return Array.from(capabilities).sort();
}
